import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const errText = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

// Staff POS: kiosk credit-approval requests + kiosk help alerts (polls every 4 s).
export function KioskStaffAlerts({ role }) {
  const [data, setData] = useState({ approvals: [], help: [] });
  const [checked, setChecked] = useState(false);
  const [snoozed, setSnoozed] = useState({});
  const allowed = ["owner", "manager", "staff"].includes(role);
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if (!allowed) return undefined;
    const chk = () => api.get("/voice/settings", { __skipAuthRedirect: true }).then((r) => setEnabled(!!r.data.kiosk_enabled)).catch(() => {});
    chk();
    const t = setInterval(chk, 60000);
    return () => clearInterval(t);
  }, [allowed]);
  useEffect(() => {
    if (!allowed || !enabled) return undefined;
    let on = true;
    const run = () => api.get("/kiosk/staff/alerts", { __skipAuthRedirect: true }).then((r) => on && setData(r.data)).catch(() => {});
    run();
    const t = setInterval(run, 4000);
    return () => { on = false; clearInterval(t); };
  }, [allowed, enabled]);
  const a = data.approvals.find((x) => !(snoozed[x.id] > Date.now()));
  useEffect(() => setChecked(false), [a?.id]);
  if (!allowed) return null;
  const decide = async (approve) => {
    try {
      await api.post(`/kiosk/staff/approvals/${a.id}`, { approve, identity_checked: checked });
      toast.success(approve ? "Credit access approved for this kiosk order" : "Credit request declined");
      setData((d) => ({ ...d, approvals: d.approvals.slice(1) }));
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <>
      {a && (
        <div className="fixed inset-0 z-[90] bg-black/45 flex items-center justify-center p-4" data-testid="kiosk-approval-dialog">
          <div className="bg-white rounded-xl w-full max-w-md p-5 shadow-2xl">
            <div className="flex items-center gap-2 font-semibold text-lg"><ShieldCheck size={22} className="text-[var(--terracotta)]" /> Kiosk credit request{a.test && <span className="ml-1 rounded-full bg-amber-100 text-amber-800 text-xs px-2 py-0.5" data-testid="kiosk-approval-test-badge">STAFF TEST — no credit is used</span>}</div>
            <div className="text-sm text-[var(--ink-500)] mt-1">{a.device_name || "AI kiosk"} · a customer wants to use account credit.</div>
            <div className="mt-4 rounded-lg bg-[var(--sand-100)] p-3 text-sm space-y-1" data-testid="kiosk-approval-customer">
              <div><b>Account:</b> {a.customer?.name}</div>
              <div><b>Phone:</b> {a.customer?.phone || "—"}</div>
              {a.customer?.email && <div><b>Email:</b> {a.customer.email}</div>}
              {a.spoken_name && a.spoken_name !== a.customer?.name && <div><b>Name given at kiosk:</b> {a.spoken_name}</div>}
            </div>
            <label className="flex items-start gap-2 mt-4 text-sm">
              <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid="kiosk-approval-identity" />
              I've checked this customer's identity in person (name + phone or ID). Balance stays hidden until approved.
            </label>
            <div className="flex gap-2 mt-5">
              <button className="h-11 px-3 rounded-md text-sm text-[var(--ink-500)]" data-testid="kiosk-approval-later" onClick={() => setSnoozed((s) => ({ ...s, [a.id]: Date.now() + 60000 }))}>Later</button>
              <button className="flex-1 h-11 rounded-md border" data-testid="kiosk-approval-decline" onClick={() => decide(false)}>Decline</button>
              <button className="flex-1 h-11 rounded-md bg-[var(--terracotta)] text-white disabled:opacity-50" disabled={!checked} data-testid="kiosk-approval-approve" onClick={() => decide(true)}>Approve</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
